/**
 * Vocabulary of outbound webhooks — TODAY → partner (TO-381).
 *
 * Distinct from the inbound ledger in `webhook.types.ts`: those enums describe
 * deliveries partners send us, these describe deliveries we send them.
 */
import type { OutboundEventType } from './outbound-webhook.catalog.js'

/** Lifecycle of one outbound delivery. Terminal states never change. */
export enum OutboundDeliveryStatus {
	PENDING = 'PENDING',
	SUCCEEDED = 'SUCCEEDED',
	FAILED = 'FAILED',
	CANCELLED = 'CANCELLED',
}

/** Why the last attempt of a delivery did not succeed. */
export enum OutboundDeliveryErrorCode {
	TIMEOUT = 'TIMEOUT',
	NETWORK_ERROR = 'NETWORK_ERROR',
	HTTP_STATUS = 'HTTP_STATUS',
	REDIRECT_NOT_FOLLOWED = 'REDIRECT_NOT_FOLLOWED',
	TARGET_ADDRESS_BLOCKED = 'TARGET_ADDRESS_BLOCKED',
}

/** Why a subscription is disabled. */
export enum SubscriptionDisabledReason {
	MANUAL = 'MANUAL',
	AUTO_FAILURES = 'AUTO_FAILURES',
}

/** Event types that are sent to partners but never travel the event bus. */
export enum OutboundSyntheticEventType {
	/** The body of an on-demand test delivery. */
	TEST = 'webhook.test',
}

/** Headers every outbound request carries (lower-case, as HTTP header names compare). */
export const OUTBOUND_SIGNATURE_HEADER = 'x-today-signature'
export const OUTBOUND_TIMESTAMP_HEADER = 'x-today-timestamp'
export const OUTBOUND_EVENT_ID_HEADER = 'x-today-event-id'
export const OUTBOUND_EVENT_TYPE_HEADER = 'x-today-event-type'
export const OUTBOUND_DELIVERY_ID_HEADER = 'x-today-delivery-id'
export const OUTBOUND_USER_AGENT = 'Today-Webhooks/1.0'

/** Prefix of the signature header value (`v1=<hex>`). */
export const OUTBOUND_SIGNATURE_VERSION = 'v1'

/** How far a receiver may accept a signed timestamp from its own clock, in seconds. */
export const OUTBOUND_TIMESTAMP_TOLERANCE_SECONDS = 300

/** Signing secrets read `whsec_` + 43 base64url characters (32 random bytes). */
export const OUTBOUND_SIGNING_SECRET_PREFIX = 'whsec_'

/** Live subscriptions a tenant may hold. */
export const OUTBOUND_MAX_SUBSCRIPTIONS_PER_TENANT = 20

/** Longest accepted `targetUrl`, in characters. */
export const OUTBOUND_TARGET_URL_MAX_LENGTH = 2048

/** Attempts per delivery, including the first. */
export const OUTBOUND_MAX_ATTEMPTS = 5

/** Wait before attempts 2..5 after a retryable failure, in seconds. */
export const OUTBOUND_RETRY_DELAYS_SECONDS: readonly number[] = [60, 300, 900, 1800]

/** Upper bound on an honoured `Retry-After`, in seconds. */
export const OUTBOUND_RETRY_AFTER_CAP_SECONDS = 3600

/** Per-attempt HTTP timeout, in milliseconds. */
export const OUTBOUND_ATTEMPT_TIMEOUT_MS = 10_000

/** Consecutive failed deliveries after which a subscription disables itself. */
export const OUTBOUND_AUTO_DISABLE_THRESHOLD = 5

/** An outbound subscription as the API returns it. Never carries secret material. */
export interface IOutboundSubscription {
	id: string
	tenantId: string
	targetUrl: string
	eventTypes: OutboundEventType[]
	enabled: boolean
	disabledReason: SubscriptionDisabledReason | null
	consecutiveFailures: number
	lastSuccessAt: Date | null
	lastFailureAt: Date | null
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

/** One outbound delivery. The body is stored but never part of this shape. */
export interface IOutboundDelivery {
	id: string
	tenantId: string
	subscriptionId: string
	eventId: string
	eventType: string
	status: OutboundDeliveryStatus
	attempts: number
	lastResponseStatus: number | null
	lastLatencyMs: number | null
	lastErrorCode: OutboundDeliveryErrorCode | null
	nextAttemptAt: Date | null
	isTest: boolean
	replayOfDeliveryId: string | null
	createdAt: Date
	completedAt: Date | null
}

/** A tenant's allowlist for one event type, as the API returns it. */
export interface IPayloadShape {
	eventType: OutboundEventType
	fields: string[]
	availableFields: string[]
	isDefault: boolean
	updatedAt: Date | null
}

/** The JSON body of every outbound request. */
export interface OutboundEventBody {
	id: string
	type: string
	occurredAt: string
	data: Record<string, unknown>
}
